import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getGroup, getGroupMembers, getGroupRoleBindings, deleteGroup } from '../services/iam';
import { toast } from '@/lib/toast';
import { useNavigate } from 'react-router-dom';
import GroupFormModal from '../components/modals/GroupFormModal';
import { Group as GroupIcon } from 'lucide-react';
import { Breadcrumbs, Button, ListPageSkeleton, PageHeader, Skeleton } from '@/components/ds-zta';
import { DetailLoadError, SectionLoadError } from '../components/DetailLoadError';
import { isNotFoundError, retryUnlessClientError } from '@/lib/apiError';
import { useDialogs } from '@/components/dialogs/DialogProvider';

const groupsCrumbs = (current: React.ReactNode) => (
  <Breadcrumbs
    items={[{ label: 'Identity' }, { label: 'Groups', href: '/groups' }, { label: current }]}
    renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
  />
);

const GroupDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const { confirm: confirmDialog } = useDialogs();

  const groupQuery = useQuery(['group', id], () => getGroup(id!), { retry: retryUnlessClientError });
  const membersQuery = useQuery(['groupMembers', id], () => getGroupMembers(id!), { retry: retryUnlessClientError });
  const rolesQuery = useQuery(['groupRoleBindings', id], () => getGroupRoleBindings(id!), { retry: retryUnlessClientError });
  const { data: groupData, isLoading: isLoadingGroup } = groupQuery;
  const { data: membersData, isLoading: isLoadingMembers } = membersQuery;
  const { data: rolesData, isLoading: isLoadingRoles } = rolesQuery;

  const deleteMutation = useMutation(() => deleteGroup(id!), {
    onSuccess: () => {
      queryClient.invalidateQueries('groups');
      toast.success('Group deleted successfully');
      navigate('/groups');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete group: ${error.message}`);
    },
  });

  const isLoading = isLoadingGroup || isLoadingMembers || isLoadingRoles;

  if (isLoading || !groupData) {
    return (
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={groupsCrumbs(isLoading ? 'Loading' : !groupQuery.error || isNotFoundError(groupQuery.error) ? 'Not found' : 'Error')}
          icon={<GroupIcon />}
          title={
            isLoading ? (
              <>
                <span className="sr-only">Loading group details</span>
                <Skeleton variant="text" width={220} />
              </>
            ) : (
              'Group'
            )
          }
        />
        {isLoading ? (
          <ListPageSkeleton filterCount={0} rowCount={6} />
        ) : (
          <DetailLoadError
            entity="group"
            error={groupQuery.error}
            icon={<GroupIcon />}
            onRetry={() => groupQuery.refetch()}
            backLabel="Back to groups"
            onBack={() => navigate('/groups')}
          />
        )}
      </div>
    );
  }

  const group = groupData;
  const members = membersData?.data || [];
  const roleBindings = rolesData || [];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={groupsCrumbs(group.name)}
        icon={<GroupIcon />}
        title={group.name}
        description={
          <>
            <span className="block text-[var(--d2d-text-muted)]">{group.key}</span>
            {group.description && <span className="block mt-1">{group.description}</span>}
          </>
        }
        secondaryActions={
          <Button variant="secondary" onClick={() => setEditModalOpen(true)}>
            Edit
          </Button>
        }
        primaryAction={
          <Button
            variant="danger"
            onClick={async () => {
              if (await confirmDialog({
                title: `Delete ${group.name}?`,
                message: `"${group.name}" will be permanently deleted. Its members lose any access granted through this group.`,
                confirmLabel: 'Delete',
                variant: 'danger',
              })) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isLoading}
            loading={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-medium mb-4 text-[var(--d2d-text-primary)]">Assigned Roles</h2>
          <div className="bg-[var(--d2d-bg-card)] shadow rounded-lg p-4">
            {rolesQuery.isError ? (
              <SectionLoadError what="roles" error={rolesQuery.error} onRetry={() => rolesQuery.refetch()} />
            ) : roleBindings.length > 0 ? (
              <ul>
                {roleBindings.map(binding => (
                  <li key={binding.id} className="p-2 text-[var(--d2d-text-primary)]">{binding.role?.name}</li>
                ))}
              </ul>
            ) : (
              <p className="text-[var(--d2d-text-muted)]">No roles assigned.</p>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium mb-4 text-[var(--d2d-text-primary)]">Members</h2>
          <div className="bg-[var(--d2d-bg-card)] shadow rounded-lg p-4">
            {membersQuery.isError ? (
              <SectionLoadError what="members" error={membersQuery.error} onRetry={() => membersQuery.refetch()} />
            ) : members.length > 0 ? (
              <ul>
                {members.map(member => (
                  <li key={member.id} className="p-2 border-b border-[var(--d2d-border)]">
                    <Link to={`/users/${member.id}`} className="text-[var(--d2d-text-primary)] hover:underline">
                      {member.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[var(--d2d-text-muted)]">No members in this group.</p>
            )}
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <GroupFormModal 
          group={group}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </div>
  );
};

export default GroupDetailPage;
